// ============================================================================
// TENANT RESOLVER - Single source of truth for active tenant_id + profile
// ----------------------------------------------------------------------------
// Every multi-tenant query in DataService MUST call getActiveTenantId() to
// enforce data isolation. This module provides a graceful default so local
// development and existing tests work without explicit tenant configuration.
//
// Resolution order:
//   1. Explicit override via setTenantId() (for testing or multi-tenant UI)
//   2. Server-verified authenticated tenant binding
//   3. Default: 'default_tenant' for unauthenticated local/test utilities
//
// When lifted to AWS (Cognito + JWT), this resolver will read tenant_id from
// the decoded JWT claims instead of localStorage. The calling code won't change.
//
// v5.13.0 (Workstream B4): adds getInstanceOwner(): returns the cached tenant
// profile (legal name, ABN, licence, email_domains, staff_emails, staff_names,
// ...). The profile is the foundation for fixing the contact-extraction bug
// where RJ Bird staff emails in a forwarded RFQ chain were classified as the
// site contact. Callers inject a TENANT_BLOCK into the extraction prompt so
// the LLM knows which contacts are own-staff.
// ============================================================================

import { getVerifiedLocalTenantId } from './local-tenant-database';

const DEFAULT_TENANT_ID = 'default_tenant';
const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min. Long enough to coast an analysis pipeline without re-fetching, short enough that a fresh Settings save takes effect without reload

let _overrideTenantId = null;

/**
 * Set an explicit tenant_id override. Useful for tests and multi-tenant admin UIs.
 * Pass null to clear the override and fall back to localStorage / default.
 */
export function setTenantId(tenantId) {
    _overrideTenantId = tenantId || null;
}

/**
 * Get the active tenant_id. Resolution order:
 *   1. Explicit override (via setTenantId)
 *   2. Server-verified authenticated tenant binding
 *   3. 'default_tenant' fallback for unauthenticated local/test utilities
 *
 * NEVER returns null or undefined - always a string.
 */
export function getActiveTenantId() {
    // Priority 1: explicit override
    if (_overrideTenantId) return _overrideTenantId;

    // Priority 2: the JWT-selected namespace stays locked until /auth/me
    // agrees with it. Never let a client-decoded or retired localStorage value
    // become active tenant authority.
    const verifiedTenantId = getVerifiedLocalTenantId();
    if (verifiedTenantId) return verifiedTenantId;

    // Priority 3: default
    return DEFAULT_TENANT_ID;
}

/**
 * Stamp a record with the active tenant_id if not already set.
 * Idempotent - does not overwrite an existing tenant_id.
 */
export function stampTenantId(record) {
    if (!record) return record;
    if (!record.tenant_id) {
        record.tenant_id = getActiveTenantId();
    }
    return record;
}

// ─── Instance owner profile (v5.13.0) ───────────────────────────────────────
//
// Cached shape (camelCase, as returned by the API translator):
//   {
//     tenantId, legalName, tradingName, abn,
//     licenceNumber, licenceJurisdiction,
//     emailDomains: string[], staffEmails: string[], staffNames: string[],
//     adminCcEmail, logoPath, primaryColor,
//     bankBsb, bankAccount, bankAccountName, termsDocId,
//     createdAt, updatedAt,
//   }
//
// Null when the profile row doesn't exist yet (fresh install before
// migration 029 runs, or non-seeded tenant). Callers MUST treat null as
// "unknown": do not synthesise a fake owner.

// One cache entry per business, keyed by the tenant the read was made for
// (G6, 2026-10-12). A single shared entry served whoever was signed in for
// five minutes, so after a same-tab switch the next business was answered
// with the previous business's row (its name, ABN, licence and colour), and
// every consumer had to remember its own tenant check. A business is now only
// ever answered with a profile read for it.
const _profileCaches = new Map(); // tenantId -> { data, fetchedAt, promise, previous }

// The last profile this business READ SUCCESSFULLY, with the tenant it belongs
// to. A failed read answers with it instead of the empty shape (D7 follow-up,
// 2026-10-06): an empty profile makes Stage 5's terms "not known" and holds
// Print, Download PDF and Email, so one flaky refetch used to stop RJ Bird
// sending its own quote. Kept through invalidateInstanceOwner() (a Settings
// save followed by a failed read still knows the business), and only ever
// returned for the tenant it was read for.
let _lastGoodProfile = null;      // { tenantId, data }

// Marks a read that did not produce a profile row, so the cache step can tell
// a failure from a real answer.
const READ_FAILED = Symbol('tenant-profile-read-failed');

function _buildEmptyOwner(tenantId = getActiveTenantId()) {
    // Safe shape for callers that would otherwise crash on undefined.
    // legalName is deliberately empty: that's the signal to the UI that
    // the profile hasn't been set up yet.
    return {
        tenantId,
        legalName: '',
        tradingName: null,
        abn: null,
        licenceNumber: null,
        licenceJurisdiction: null,
        emailDomains: [],
        staffEmails: [],
        staffNames: [],
        adminCcEmail: null,
        logoPath: null,
        primaryColor: null,
        bankBsb: null,
        bankAccount: null,
        bankAccountName: null,
        termsDocId: null,
        contactName: null,
        contactPosition: null,
        contactPhone: null,
        contactEmail: null,
        postalAddress: null,
        complianceLine: null,
        quoteNumberPrefix: null,
        // Unknown, so quotes print the standard warranty line.
        quoteWarrantyNote: null,
        // WO-D / D7 (2026-04-25): tenant GST display preference. Defaults to
        // 'inclusive' so brand-new installs match RJ BIRD's current behaviour
        // (inc-GST hero label). Valid values: 'inclusive' | 'exclusive'.
        gstDisplayMode: 'inclusive',
        // Unknown, so a strata quote keeps the client company (see
        // addressesStrataQuotesToOwners in utils/tenant-quote-defaults.js).
        strataQuotesToOwners: null,
    };
}

/**
 * Fetch and cache the instance owner's profile. Returns a Promise that
 * resolves to the profile object. NEVER null, so callers don't need to
 * null-guard. When the read fails (or returns no row) it resolves to the last
 * profile this business read successfully, or the empty shape if it has never
 * read one.
 *
 * Cache TTL: 5 minutes, per business (the active tenant). invalidateInstanceOwner()
 * resets the cache: call it after a Settings save so the next analysis
 * pipeline sees the new values.
 *
 * Safe to call from any module: lazy-imports data-service-api to avoid a
 * circular dependency (data-service-api itself imports nothing from here
 * today but the door is open).
 */
export function getInstanceOwner({ force = false } = {}) {
    const now = Date.now();
    const requestedTenantId = getActiveTenantId();
    const cached = _profileCaches.get(requestedTenantId) || null;
    if (!force && cached?.data && (now - cached.fetchedAt) < PROFILE_CACHE_TTL_MS) {
        return Promise.resolve(cached.data);
    }
    if (cached?.promise && !force) {
        return cached.promise;
    }

    const outcome = (async () => {
        try {
            const mod = await import('./data-service-api');
            const api = mod?.DataServiceAPI;
            if (!api?.tenantProfile?.get) {
                // API surface missing: test environment or backend disabled.
                return READ_FAILED;
            }
            const row = await api.tenantProfile.get();
            if (!row || typeof row !== 'object') {
                // get() swallows every request failure to null, so a missing
                // row here is almost always a failed read.
                return READ_FAILED;
            }
            // Normalise array fields (Postgres returns them as arrays already,
            // but be defensive in case the row came back pre-translated or via
            // a future REST wrapper).
            return {
                tenantId: row.tenantId || row.tenant_id || requestedTenantId,
                legalName: row.legalName || row.legal_name || '',
                tradingName: row.tradingName ?? row.trading_name ?? null,
                abn: row.abn ?? null,
                licenceNumber: row.licenceNumber ?? row.licence_number ?? null,
                licenceJurisdiction: row.licenceJurisdiction ?? row.licence_jurisdiction ?? null,
                emailDomains: Array.isArray(row.emailDomains) ? row.emailDomains
                    : (Array.isArray(row.email_domains) ? row.email_domains : []),
                staffEmails: Array.isArray(row.staffEmails) ? row.staffEmails
                    : (Array.isArray(row.staff_emails) ? row.staff_emails : []),
                staffNames: Array.isArray(row.staffNames) ? row.staffNames
                    : (Array.isArray(row.staff_names) ? row.staff_names : []),
                adminCcEmail: row.adminCcEmail ?? row.admin_cc_email ?? null,
                logoPath: row.logoPath ?? row.logo_path ?? null,
                primaryColor: row.primaryColor ?? row.primary_color ?? null,
                bankBsb: row.bankBsb ?? row.bank_bsb ?? null,
                bankAccount: row.bankAccount ?? row.bank_account ?? null,
                bankAccountName: row.bankAccountName ?? row.bank_account_name ?? null,
                termsDocId: row.termsDocId ?? row.terms_doc_id ?? null,
                // Letterhead fields (migration 095). Before these existed, the
                // quote header hardcoded Customer Zero's contact name, phone,
                // email, postal address and compliance line, so every other
                // client's quote carried them.
                contactName: row.contactName ?? row.contact_name ?? null,
                contactPosition: row.contactPosition ?? row.contact_position ?? null,
                contactPhone: row.contactPhone ?? row.contact_phone ?? null,
                contactEmail: row.contactEmail ?? row.contact_email ?? null,
                postalAddress: row.postalAddress ?? row.postal_address ?? null,
                complianceLine: row.complianceLine ?? row.compliance_line ?? null,
                // Two-tenant launch D3 (2026-08-13): tenant-driven quote-number
                // display prefix (migration 091). null = the business name's
                // initials (services/business-quote-prefix.ts), never 'RBB'.
                quoteNumberPrefix: row.quoteNumberPrefix ?? row.quote_number_prefix ?? null,
                // D5 (migration 131): the business's own warranty line, printed
                // after "NOTE: ". null = the standard line
                // (services/business-warranty-note.ts), never RJ Bird's.
                quoteWarrantyNote: row.quoteWarrantyNote ?? row.quote_warranty_note ?? null,
                // WO-D / D7 (2026-04-25): tenant GST display preference.
                // Migration 034 adds the column with DEFAULT 'inclusive'; the
                // fallback here also defaults to 'inclusive' so callers that
                // hit this resolver before the migration runs (or against an
                // older backend build) get the safe / RJ-BIRD-equivalent
                // behaviour instead of undefined.
                gstDisplayMode: (
                    row.gstDisplayMode === 'exclusive' || row.gst_display_mode === 'exclusive'
                        ? 'exclusive'
                        : 'inclusive'
                ),
                // Set per business in the Control Panel (migration 127). null
                // when the backend predates that column: only a real boolean
                // may decide the strata addressee.
                strataQuotesToOwners: typeof row.strataQuotesToOwners === 'boolean' ? row.strataQuotesToOwners
                    : (typeof row.strata_quotes_to_owners === 'boolean' ? row.strata_quotes_to_owners : null),
            };
        } catch (err) {
            // why: a 401 during dev-auto-login OR a cold-start race where the
            // backend isn't ready yet would otherwise throw and cascade into
            // a Stage 1 crash. The caller gets the last good profile of this
            // business, or the empty shape when it has none, and is expected
            // to degrade gracefully on the empty shape.
            console.warn('[tenant-resolver] getInstanceOwner failed, keeping the last good profile or returning empty:', err?.message || err);
            return READ_FAILED;
        }
    })().then((result) => {
        if (result !== READ_FAILED) {
            _lastGoodProfile = { tenantId: requestedTenantId, data: result };
            return { data: result, ok: true };
        }
        const lastGood = _lastGoodProfile && _lastGoodProfile.tenantId === requestedTenantId
            ? _lastGoodProfile.data
            : null;
        return { data: lastGood || _buildEmptyOwner(requestedTenantId), ok: false };
    });

    // `previous` keeps the last profile readable through getInstanceOwnerCached()
    // while this fetch is in flight. Without it the synchronous read went null
    // for the length of every refetch, and a quote title generated in that
    // moment took RJ Bird's RBB prefix instead of the business's own.
    const previous = cached?.data ?? cached?.previous ?? null;
    const previousFetchedAt = cached?.data ? cached.fetchedAt : 0;
    // Registered before the caller's promise is derived, so the cache is
    // updated before any awaiting caller resumes. The answer lands in the
    // entry of the business it was read for, even if another business is
    // active by the time it arrives.
    outcome.then(({ data, ok }) => {
        // A failed read keeps serving what it answered with, but is never
        // fresh: the next call reads again instead of trusting it for the TTL.
        _profileCaches.set(requestedTenantId, { data, fetchedAt: ok ? Date.now() : previousFetchedAt, promise: null });
    }, () => {
        // Not reached (every failure resolves above); clear the in-flight
        // marker so a subsequent call can retry.
        _profileCaches.delete(requestedTenantId);
    });
    const promise = outcome.then(({ data }) => data);
    _profileCaches.set(requestedTenantId, { data: null, fetchedAt: now, promise, previous });
    return promise;
}

/**
 * Clear the cached instance-owner profile. Call this after a Settings save
 * so the next analysis pipeline picks up the updated values immediately.
 */
export function invalidateInstanceOwner() {
    _profileCaches.clear();
}

/**
 * Synchronous read of the cached profile — null until the first
 * getInstanceOwner() resolves (it is warmed at app start). For call sites
 * that cannot await (render-path formatters like formatQmQuoteNoDisplay);
 * callers MUST treat null as "use compile-time brand defaults".
 *
 * After invalidateInstanceOwner() (every Settings save) this is null until
 * the refetch settles, on purpose: the quote prefix reads it first and the
 * prefix SettingsPage just saved second, so a pre-save profile here would put
 * the old prefix on a new title.
 *
 * `orLastGood: true` answers in that window with the last profile this
 * business read successfully (only ever the current tenant's). Stage 5 seeds
 * from it: without it a Settings save followed by a slow or stalled refetch
 * held RJ Bird's Print, PDF and Email until the read settled, although the tab
 * had already read RJ Bird's profile. It is the same profile a failed refetch
 * answers with (getInstanceOwner above).
 */
export function getInstanceOwnerCached({ orLastGood = false } = {}) {
    const tenantId = getActiveTenantId();
    const entry = _profileCaches.get(tenantId);
    const cached = entry?.data ?? entry?.previous ?? null;
    if (cached || !orLastGood) return cached;
    return _lastGoodProfile && _lastGoodProfile.tenantId === tenantId
        ? _lastGoodProfile.data
        : null;
}

/**
 * Build the TENANT_BLOCK string injected into contact-extraction (and any
 * future tenant-aware prompts). Shape:
 *
 *   legal_name: RJ BIRD BUILDING PTY LTD
 *   trading_name: null
 *   email_domains: [rjbirdbuilding.com.au]
 *   staff_emails: [peter@rjbirdbuilding.com.au]
 *   staff_names: [Peter Bird]
 *   abn: 48 606 543 382
 *   licence: 285018C (NSW)
 *
 * Returns a Promise<string>. When the profile row is empty (unseeded install),
 * returns a single "(no instance-owner profile configured)" line so the
 * prompt never contains a confusing blank block.
 */
export async function buildTenantBlock() {
    const owner = await getInstanceOwner();
    if (!owner || !owner.legalName) {
        return '(no instance-owner profile configured)';
    }
    const fmtList = (arr) => (Array.isArray(arr) && arr.length) ? `[${arr.join(', ')}]` : '[]';
    const licence = owner.licenceNumber
        ? (owner.licenceJurisdiction ? `${owner.licenceNumber} (${owner.licenceJurisdiction})` : owner.licenceNumber)
        : 'null';
    return [
        `legal_name: ${owner.legalName}`,
        `trading_name: ${owner.tradingName || 'null'}`,
        `email_domains: ${fmtList(owner.emailDomains)}`,
        `staff_emails: ${fmtList(owner.staffEmails)}`,
        `staff_names: ${fmtList(owner.staffNames)}`,
        `abn: ${owner.abn || 'null'}`,
        `licence: ${licence}`,
    ].join('\n');
}

// Expose for tests
export { DEFAULT_TENANT_ID };
